import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useData } from "../../providers/DataProvider";
import { useNavigate } from "react-router-dom";

interface BonusTypeSwitcherProps {
  onBonusTypeChange?: (newType: string) => void;
}

const BonusTypeSwitcher: React.FC<BonusTypeSwitcherProps> = ({ onBonusTypeChange }) => {
  const { bonusType, setBonusType, role, isBonusTypeLoading } = useData();
  const [isOpen, setIsOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Which item to focus when opening via keyboard
  const focusTargetOnOpen = useRef<"first" | "last">("first");

  const navigate = useNavigate();

  const restrictedRoles = useMemo(() => ["Ops", "PM", "Uploader"], []);
  const isRestrictedRole = !!role && restrictedRoles.includes(role);

  const bonusTypes = useMemo(
    () =>
      isRestrictedRole
        ? [{ label: "Closing Bonus", value: "ClosingBonus" }]
        : [
          { label: "Closing Bonus", value: "ClosingBonus" },
          { label: "Stage 5 Bonus", value: "Stage5Bonus" },
        ],
    [isRestrictedRole]
  );

  const currentLabel = useMemo(() => {
    const match = bonusTypes.find((t) => t.value === bonusType);
    if (!match && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        `[BonusTypeSwitcher] Unknown bonusType "${bonusType}". Falling back to "${bonusTypes[0].label}".`
      );
    }
    return match?.label ?? bonusTypes[0].label;
  }, [bonusTypes, bonusType]);

  const closeMenu = useCallback((returnFocus = true) => {
    setIsOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  // Close on outside click (only when open)
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu(false);
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, closeMenu]);

  // Focus desired item when menu opens
  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;

    const items = Array.from(
      dropdownRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]')
    );
    if (items.length === 0) return;

    if (focusTargetOnOpen.current === "last") {
      items[items.length - 1]?.focus();
    } else {
      items[0]?.focus();
    }
  }, [isOpen]);

  // Enforce ClosingBonus for restricted roles
  useEffect(() => {
    if (isRestrictedRole && bonusType !== "ClosingBonus") {
      setBonusType("ClosingBonus");
      setIsOpen(false);
      navigate("/", { replace: true });
    }
  }, [isRestrictedRole, bonusType, setBonusType, navigate]);

  const handleSelect = useCallback(
    (newType: string) => {
      if (newType !== bonusType) {
        setBonusType(newType);
        onBonusTypeChange?.(newType);
        navigate("/", { replace: true });
      }
      closeMenu(true);
    },
    [bonusType, setBonusType, onBonusTypeChange, navigate, closeMenu]
  );

  const handleMenuKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!dropdownRef.current) return;

      const items = Array.from(
        dropdownRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]')
      );
      if (items.length === 0) return;

      const currentIndex = items.findIndex((item) => item === document.activeElement);

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          closeMenu(true);
          break;

        case "ArrowDown":
          e.preventDefault();
          items[(currentIndex + 1 + items.length) % items.length]?.focus();
          break;

        case "ArrowUp":
          e.preventDefault();
          items[(currentIndex - 1 + items.length) % items.length]?.focus();
          break;

        case "Home":
          e.preventDefault();
          items[0]?.focus();
          break;

        case "End":
          e.preventDefault();
          items[items.length - 1]?.focus();
          break;

        case "Tab":
          closeMenu(false);
          break;

        default:
          break;
      }
    },
    [closeMenu]
  );

  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (isRestrictedRole) return;

      // Close if open
      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        closeMenu(true);
        return;
      }

      // Open patterns
      if (!isOpen) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          focusTargetOnOpen.current = "first";
          setIsOpen(true);
          return;
        }

        if (e.key === "ArrowDown") {
          e.preventDefault();
          focusTargetOnOpen.current = "first";
          setIsOpen(true);
          return;
        }

        if (e.key === "ArrowUp") {
          e.preventDefault();
          focusTargetOnOpen.current = "last";
          setIsOpen(true);
          return;
        }
      }
    },
    [isOpen, isRestrictedRole, closeMenu]
  );

  const dropdownId = "bonus-type-switcher-menu";

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (isRestrictedRole || isBonusTypeLoading) return;
          focusTargetOnOpen.current = "first";
          setIsOpen((v) => !v);
        }}
        onKeyDown={handleTriggerKeyDown}
        disabled={isRestrictedRole || isBonusTypeLoading}
        className={`flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out ${isRestrictedRole || isBonusTypeLoading
            ? "opacity-75 cursor-not-allowed"
            : ""
          }`}
        aria-haspopup={!isRestrictedRole && !isBonusTypeLoading ? "menu" : undefined}
        aria-expanded={!isRestrictedRole && !isBonusTypeLoading ? isOpen : undefined}
        aria-controls={!isRestrictedRole && !isBonusTypeLoading ? dropdownId : undefined}
        aria-busy={isBonusTypeLoading}
      >
        {isBonusTypeLoading ? (
          <>
            <svg
              className="animate-spin w-4 h-4 mr-2 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
              />
            </svg>
            <span>Loading…</span>
          </>
        ) : (
          <>
            <span className="mr-2">{currentLabel}</span>
            {!isRestrictedRole && (
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </>
        )}
      </button>

      {isOpen && !isRestrictedRole && (
        <div
          id={dropdownId}
          ref={dropdownRef}
          role="menu"
          className="absolute right-0 mt-2 py-2 w-48 bg-white rounded-md shadow-xl z-20"
        >
          {bonusTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => handleSelect(type.value)}
              onKeyDown={handleMenuKeyDown}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left transition duration-150 ease-in-out"
              role="menuitem"
              tabIndex={-1}
            >
              {type.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BonusTypeSwitcher;
